/**
 * Server-rendered version family + related documents, as real `<a href>`.
 *
 * The interactive Related/History tabs (DocumentTabs) hydrate client-side
 * only, so before this a crawler fetching a document page saw zero links to
 * the rest of its version family — the supersedes/superseded-by chain existed
 * in the backend graph and nowhere in the HTML. This is the same data,
 * rendered flat and unconditionally so it is present in the server response:
 * the internal link graph gains the edges that tell a crawler these documents
 * are versions of one another, and a reader gets "this was superseded by / this
 * supersedes / related to" without opening a tab.
 *
 * It sits ALONGSIDE DocumentTabs, not instead of it: the tabs keep the graph
 * modal and the richer grouping for people; this guarantees the links exist in
 * the HTML for everyone else. Each version is a distinct, self-canonical page
 * (a superseded document is not a duplicate of the current one), so these are
 * plain links — never `rel=canonical`, never `sameAs`.
 *
 * Rendered as a native `<details>`, collapsed by default (BL-0015): a reader
 * who already has the History tab open was seeing the same supersession list
 * twice, once in the tab and once always-open below it. `<details>` content is
 * present in the DOM either way — unlike `display:none`, Google credits it the
 * same as visible text — so collapsing it removes the visual duplication
 * without touching what a crawler sees.
 */
import React from 'react';
import { UniversalLink } from '@plone/volto/components';
import { flattenToAppURL } from '@plone/volto/helpers';

import { extractRelations } from './relations';
import { hasFamily, timelineUrlOf } from '../FamilyTimeline/url';

const SECTIONS = [
  {
    key: 'supersededBy',
    heading: 'Superseded by',
    hint: 'A newer version replaces this document.',
  },
  {
    key: 'supersedes',
    heading: 'Supersedes',
    hint: 'Earlier documents this one replaced.',
  },
  { key: 'related', heading: 'Related documents', hint: null },
];

const Relist = ({ items }) => (
  <ul className="pp-relations__list">
    {items.map((it) => (
      <li className="pp-relations__item" key={it.href}>
        <UniversalLink href={it.href} className="pp-relations__link">
          {it.docid ? (
            <span className="pp-relations__docid">{it.docid}</span>
          ) : null}
          <span className="pp-relations__title">{it.title}</span>
        </UniversalLink>
        {it.date ? (
          <span className="pp-relations__date"> ({it.date})</span>
        ) : null}
      </li>
    ))}
  </ul>
);

const VersionRelations = ({ content }) => {
  // Visible list needs only app-relative hrefs, so no site/origin is required;
  // the JSON-LD path (DocumentSEO) passes the site for absolute URLs.
  const groups = extractRelations(content);
  const present = SECTIONS.filter(({ key }) => groups[key].length > 0);
  // The hub link is decided by the WHOLE family (the recursive graph), not by
  // the single-hop sections above: a document whose only relation is "cancels"
  // has no section here and still belongs to a family worth linking to.
  const family = hasFamily(content);
  if (present.length === 0 && !family) return null;

  return (
    <details
      className="pp-relations"
      aria-label="Version history and related documents"
    >
      <summary className="pp-relations__summary">
        Version history and related documents
      </summary>
      <div className="pp-relations__body">
        {present.map(({ key, heading, hint }) => (
          <div className="pp-relations__group" key={key}>
            <h2 className="pp-relations__heading">{heading}</h2>
            {hint ? <p className="pp-relations__hint">{hint}</p> : null}
            <Relist items={groups[key]} />
          </div>
        ))}
        {/* One link, and only when there is more than one document to show:
            the sections above stop after a single hop, this reaches the whole
            chronology. See components/FamilyTimeline. */}
        {family ? (
          <p className="pp-relations__more">
            <UniversalLink
              href={timelineUrlOf(flattenToAppURL(content['@id'] || ''))}
              className="pp-relations__morelink"
            >
              View full version history
            </UniversalLink>
          </p>
        ) : null}
      </div>
    </details>
  );
};

export default VersionRelations;
